import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { roundsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isCumulativeRound, hasBowl, championLabel } from '../utils/eventTypes';

/**
 * RoundResults page — shows Stableford, Gross, and Fivers tabs for a completed round.
 * Route: /rounds/:roundId/results
 * Expects location.state.backLabel for contextual back button.
 */
export default function RoundResults() {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const backLabel = location.state?.backLabel || 'Back';

  const [activeTab, setActiveTab] = useState('stableford');
  const [round, setRound] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [results, setResults] = useState(null);
  const [playoffStatus, setPlayoffStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [declaringWinner, setDeclaringWinner] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [roundId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [roundData, leaderboardData, resultsData] = await Promise.all([
        roundsAPI.getById(roundId),
        roundsAPI.getLeaderboard(roundId),
        roundsAPI.getResults(roundId)
      ]);
      setRound(roundData);
      setLeaderboard(leaderboardData || []);
      setResults(resultsData);

      // Fetch playoff status for completed TML R2
      if (
        roundData.status === 'completed' &&
        roundData.event_type === 'tml' &&
        roundData.round_type === 'championship'
      ) {
        try {
          const ps = await roundsAPI.getPlayoffStatus(roundId);
          setPlayoffStatus(ps);
        } catch {
          // Non-fatal
        }
      }
    } catch (err) {
      setError('Failed to load results: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeclareWinner = async (playerId, playerName, type) => {
    if (!window.confirm(`Declare ${playerName} as ${type === 'bowl' ? 'bowl' : 'championship'} playoff winner?`)) return;
    try {
      setDeclaringWinner(true);
      const result = await roundsAPI.setPlayoffWinner(roundId, playerId, type);
      setPlayoffStatus(result.playoff_status);
    } catch (err) {
      alert('Failed to declare winner: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeclaringWinner(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !round) {
    return (
      <div className="px-4 py-8">
        <p className="text-red-600 font-semibold">{error || 'Round not found'}</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-primary font-semibold">
          Go Back
        </button>
      </div>
    );
  }

  const isTmlChampionship = round.event_type === 'tml' && round.round_type === 'championship';
  const isCompleted = round.status === 'completed';

  // Determine if R2 (has prior championship round — any player's total != current_round_points)
  const isR2 = isTmlChampionship &&
    (leaderboard || []).some(p => Number(p.total_points || 0) !== Number(p.current_round_points || 0));

  // Show playoff section only on completed R2 TML championship, for admins
  const showPlayoffSection = isAuthenticated && isCompleted && isTmlChampionship && isR2;

  return (
    <div className="pb-20">
      {/* Back button */}
      <div className="px-4 pt-4 mb-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-primary font-semibold hover:text-secondary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {backLabel}
        </button>
      </div>

      {/* Round header */}
      <div className="px-4 mb-4">
        <h1 className="text-2xl font-bold text-gray-900">{round.event_name || 'Round'}</h1>
        <p className="text-sm text-gray-500">{round.course_name} · Results</p>
      </div>

      {/* Tabs */}
      <div className="px-4 mb-4">
        <div className="flex rounded-xl bg-gray-100 p-1 gap-1">
          {['stableford', 'gross', 'fivers'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'fivers' ? 'Fivers' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'stableford' && (
        <StablefordTab leaderboard={leaderboard} round={round} roundId={roundId} navigate={navigate} />
      )}
      {activeTab === 'gross' && (
        <GrossTab gross={results?.gross || []} />
      )}
      {activeTab === 'fivers' && (
        <FiversTab fivers={results?.fivers} />
      )}

      {/* Playoff section (admin only, completed R2 TML) */}
      {showPlayoffSection && playoffStatus && (
        <PlayoffSection
          playoffStatus={playoffStatus}
          onDeclareWinner={handleDeclareWinner}
          declaringWinner={declaringWinner}
        />
      )}
    </div>
  );
}

function BowlIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 48 54" fill="none" aria-label="Bowl" style={{ flexShrink: 0 }}>
      <circle cx="5.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <circle cx="42.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <path d="M7 13 Q24 8 41 13 L36 27 Q24 31 12 27 Z" fill="#C8C8C8" stroke="#A0A0A0" strokeWidth="0.8" strokeLinejoin="round"/>
      <ellipse cx="24" cy="13" rx="17" ry="3.5" fill="#E0E0E0" stroke="#A0A0A0" strokeWidth="0.7"/>
      <rect x="21.5" y="27" width="5" height="5" rx="1" fill="#C8C8C8"/>
      <ellipse cx="24" cy="33" rx="6.5" ry="3" fill="#D0D0D0"/>
      <rect x="14" y="36" width="20" height="3.5" rx="1" fill="#C0C0C0"/>
      <rect x="11" y="39.5" width="26" height="5" rx="2" fill="#B0B0B0"/>
    </svg>
  );
}

function StablefordTab({ leaderboard, round, roundId, navigate }) {
  const isCumulative = isCumulativeRound(round);
  const showBowl = hasBowl(round?.event_type);
  const isChampionshipRound2Plus = isCumulative &&
    (leaderboard || []).some(p => Number(p.total_points || 0) !== Number(p.current_round_points || 0));
  const isCompleted = round?.status === 'completed';

  const leaderboardWithPositions = (leaderboard || []).map((player, index, arr) => {
    let displayPosition = index + 1;
    if (index > 0) {
      const prev = arr[index - 1];
      if (player.total_points === prev.total_points && player.total_strokes === prev.total_strokes) {
        displayPosition = arr[index - 1].displayPosition ?? index;
      }
    }
    return { ...player, displayPosition };
  });

  const bowlChampionId = isCompleted && isChampionshipRound2Plus && showBowl
    ? leaderboardWithPositions.find(p => p.is_bowl)?.player_id
    : null;

  if (!leaderboard || leaderboard.length === 0) {
    return (
      <div className="px-4">
        <p className="text-gray-500 text-center py-8">No scores yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mx-4">
      {/* Header */}
      <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-semibold text-gray-700 uppercase tracking-wide">
        <div className="w-12 shrink-0 text-center">Pos</div>
        <div className="flex-1">Player</div>
        <div className="w-16 shrink-0 text-center">Pts</div>
        {isChampionshipRound2Plus && <div className="w-16 shrink-0 text-center">Rnd</div>}
      </div>

      {leaderboardWithPositions.map((player, index) => {
        const isTied = index > 0 && player.displayPosition === leaderboardWithPositions[index - 1].displayPosition;
        const isChampion = isCompleted && isChampionshipRound2Plus && player.displayPosition === 1 && !isTied;
        const isBowlChampion = player.player_id === bowlChampionId;

        return (
          <button
            key={player.round_player_id}
            onClick={() => navigate(`/rounds/${roundId}/players/${player.player_id}`)}
            className="w-full px-4 py-3 flex items-center border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50 text-left"
          >
            <div className="w-12 shrink-0 text-center text-sm font-semibold text-gray-600">
              {isTied && <span className="text-xs">T</span>}{player.displayPosition}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-gray-900 truncate" style={{ fontSize: '18px' }}>
                  {player.player_name}
                </span>
                {showBowl && player.is_bowl && <BowlIcon />}
              </div>
              {isChampion && (
                <p className="text-xs font-semibold text-gray-600 mt-0.5">
                  {championLabel(round?.event_type)}
                  {player.is_playoff_winner && ' · winner after playoff'}
                </p>
              )}
              {isBowlChampion && (
                <p className="text-xs font-semibold text-gray-500 mt-0.5">
                  Winner of the Bowl
                  {player.is_bowl_playoff_winner && ' · winner after playoff'}
                </p>
              )}
            </div>
            <div className="w-16 shrink-0 text-center font-bold text-red-600" style={{ fontSize: '18px' }}>
              {player.total_points}
            </div>
            {isChampionshipRound2Plus && (
              <div className="w-16 shrink-0 text-center text-sm text-gray-500">
                {player.current_round_points || 0}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function GrossTab({ gross }) {
  if (!gross || gross.length === 0) {
    return (
      <div className="px-4">
        <p className="text-gray-500 text-center py-8">No scores recorded</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mx-4">
      <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-semibold text-gray-700 uppercase tracking-wide">
        <div className="w-12 shrink-0 text-center">Pos</div>
        <div className="flex-1">Player</div>
        <div className="w-20 shrink-0 text-center">Strokes</div>
      </div>
      {gross.map((player, index) => (
        <div
          key={player.player_id}
          className="px-4 py-3 flex items-center border-b border-gray-100 last:border-0"
        >
          <div className="w-12 shrink-0 text-center text-sm font-semibold text-gray-600">
            {player.is_nr ? '' : player.rank}
          </div>
          <div className="flex-1 font-bold text-gray-900" style={{ fontSize: '18px' }}>
            {player.player_name}
          </div>
          <div className="w-20 shrink-0 text-center font-semibold text-gray-700" style={{ fontSize: '18px' }}>
            {player.is_nr ? <span className="text-gray-400 text-sm font-normal">NR</span> : player.total_strokes}
          </div>
        </div>
      ))}
    </div>
  );
}

function FiversTab({ fivers }) {
  if (!fivers) {
    return (
      <div className="px-4">
        <p className="text-gray-500 text-center py-8">No fivers data available</p>
      </div>
    );
  }

  return (
    <div className="px-4 space-y-4">
      <FiversSegment label="Front 9" segment={fivers.front9} />
      <FiversSegment label="Back 9" segment={fivers.back9} />
      <FiversSegment label="Overall" segment={fivers.overall} />
    </div>
  );
}

function FiversSegment({ label, segment }) {
  if (!segment) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-900 text-base">{label}</h3>
        <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
          £{segment.pot_total}
        </span>
      </div>

      {segment.is_shared ? (
        <>
          <p className="text-sm font-semibold text-amber-700 mb-3">
            Pot shared — countback couldn't separate
          </p>
          {segment.tied_players && (
            <CountbackTable players={segment.tied_players} />
          )}
        </>
      ) : segment.winner ? (
        <>
          <p className="text-lg font-bold text-gray-900">{segment.winner}</p>
          {segment.is_playoff && (
            <p className="text-xs text-gray-500 mt-0.5 mb-2">Winner · settled by countback</p>
          )}
          {segment.is_playoff && segment.tied_players && (
            <CountbackTable players={segment.tied_players} />
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">No winner determined</p>
      )}
    </div>
  );
}

function CountbackTable({ players }) {
  return (
    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 grid grid-cols-4 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <div className="col-span-1">Player</div>
        <div className="text-center">Last 6</div>
        <div className="text-center">Last 3</div>
        <div className="text-center">Last 1</div>
      </div>
      {players.map((p, i) => (
        <div key={i} className="grid grid-cols-4 px-3 py-2 border-t border-gray-100 text-sm">
          <div className="col-span-1 font-semibold text-gray-900 truncate">{p.player_name}</div>
          <div className="text-center text-gray-700">{p.last6}</div>
          <div className="text-center text-gray-700">{p.last3}</div>
          <div className="text-center text-gray-700">{p.last1}</div>
        </div>
      ))}
    </div>
  );
}

function PlayoffSection({ playoffStatus, onDeclareWinner, declaringWinner }) {
  const {
    championship_tied,
    tied_championship_players,
    bowl_tied,
    tied_bowl_players,
    championship_playoff_winner,
    bowl_playoff_winner
  } = playoffStatus;

  const hasAnything =
    (championship_tied && !championship_playoff_winner) ||
    (bowl_tied && !bowl_playoff_winner) ||
    championship_playoff_winner ||
    bowl_playoff_winner;

  if (!hasAnything) return null;

  return (
    <div className="px-4 mt-6 space-y-3">
      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Playoff Results</h2>

      {/* Championship playoff */}
      {championship_tied && !championship_playoff_winner && (
        <div className="border-2 border-yellow-400 rounded-xl p-4 bg-yellow-50">
          <p className="text-sm font-bold text-yellow-900 mb-1">Championship Playoff Required</p>
          <p className="text-xs text-yellow-700 mb-3">
            {tied_championship_players.map(p => p.player_name).join(' & ')} are tied.
            Declare the playoff winner.
          </p>
          <div className="space-y-2">
            {tied_championship_players.map(p => (
              <button
                key={p.player_id}
                onClick={() => onDeclareWinner(p.player_id, p.player_name, 'championship')}
                disabled={declaringWinner}
                className="w-full py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                {p.player_name} wins the championship playoff
              </button>
            ))}
          </div>
        </div>
      )}

      {championship_playoff_winner && (
        <div className="border border-green-300 rounded-xl p-4 bg-green-50">
          <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Championship Playoff</p>
          <p className="text-lg font-bold text-green-900">{championship_playoff_winner.player_name}</p>
          <p className="text-xs text-green-700">Winner after playoff</p>
        </div>
      )}

      {/* Bowl playoff */}
      {bowl_tied && !bowl_playoff_winner && (
        <div className="border-2 border-gray-400 rounded-xl p-4 bg-gray-50">
          <p className="text-sm font-bold text-gray-800 mb-1">Bowl Playoff Required</p>
          <p className="text-xs text-gray-600 mb-3">
            {tied_bowl_players.map(p => p.player_name).join(' & ')} are tied.
            Declare the bowl playoff winner.
          </p>
          <div className="space-y-2">
            {tied_bowl_players.map(p => (
              <button
                key={p.player_id}
                onClick={() => onDeclareWinner(p.player_id, p.player_name, 'bowl')}
                disabled={declaringWinner}
                className="w-full py-2.5 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                {p.player_name} wins the bowl playoff
              </button>
            ))}
          </div>
        </div>
      )}

      {bowl_playoff_winner && (
        <div className="border border-gray-300 rounded-xl p-4 bg-gray-50">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Bowl Playoff</p>
          <p className="text-lg font-bold text-gray-900">{bowl_playoff_winner.player_name}</p>
          <p className="text-xs text-gray-600">Bowl winner after playoff</p>
        </div>
      )}
    </div>
  );
}
